import { GoogleGenerativeAI } from '@google/generative-ai';

/**
 * 使用Gemini嵌入模型将文本转换为向量
 * @param {string} text - 需要转换为向量的文本
 * @param {string} apiKey - Gemini API密钥
 * @returns {Promise<number[]>} - 返回文本的向量表示
 */
export const getEmbedding = (text, apiKey) => {
  return new Promise((resolve, reject) => {
    requestQueue.push({ text, apiKey, resolve, reject });
    processQueue();
  });
};

/**
 * 计算两个向量之间的余弦相似度
 * @param {number[]} vecA - 第一个向量
 * @param {number[]} vecB - 第二个向量
 * @returns {number} - 返回两个向量之间的余弦相似度 (-1 到 1 之间)
 */
export const cosineSimilarity = (vecA, vecB) => {
  console.log('计算余弦相似度，向量A:', typeof vecA, vecA);
  console.log('计算余弦相似度，向量B:', typeof vecB, vecB);
  
  if (!vecA || !vecB) {
    console.error('向量为空:', { vecA, vecB });
    throw new Error('向量为空');
  }

  // 确保向量A是数组
  let vecAArray = vecA;
  if (!Array.isArray(vecA) && typeof vecA === 'object') {
    // 如果是对象但不是数组，尝试转换为数组
    if (vecA.values && Array.isArray(vecA.values)) {
      vecAArray = vecA.values;
    } else if (vecA.embedding && Array.isArray(vecA.embedding.values)) {
      vecAArray = vecA.embedding.values;
    } else {
      console.error('无法将向量A转换为数组:', vecA);
      throw new Error('无效的向量A格式');
    }
  }

  // 确保向量B是数组
  let vecBArray = vecB;
  if (!Array.isArray(vecB) && typeof vecB === 'object') {
    // 如果是对象但不是数组，尝试转换为数组
    if (vecB.values && Array.isArray(vecB.values)) {
      vecBArray = vecB.values;
    } else if (vecB.embedding && Array.isArray(vecB.embedding.values)) {
      vecBArray = vecB.embedding.values;
    } else {
      console.error('无法将向量B转换为数组:', vecB);
      throw new Error('无效的向量B格式');
    }
  }

  // 最终检查确保两个向量都是数组
  if (!Array.isArray(vecAArray) || !Array.isArray(vecBArray)) {
    console.error('无效的向量格式:', {
      vecAType: typeof vecAArray,
      vecBType: typeof vecBArray,
      isVecAArray: Array.isArray(vecAArray),
      isVecBArray: Array.isArray(vecBArray)
    });
    throw new Error('无效的向量格式');
  }
  
  if (vecAArray.length !== vecBArray.length) {
    console.error('向量长度不匹配:', {
      vecALength: vecAArray.length,
      vecBLength: vecBArray.length
    });
    throw new Error('向量长度不匹配');
  }
  
  let dotProduct = 0;
  let normA = 0;
  let normB = 0;
  
  for (let i = 0; i < vecAArray.length; i++) {
    dotProduct += vecAArray[i] * vecBArray[i];
    normA += vecAArray[i] * vecAArray[i];
    normB += vecBArray[i] * vecBArray[i];
  }
  
  normA = Math.sqrt(normA);
  normB = Math.sqrt(normB);
  
  if (normA === 0 || normB === 0) {
    return 0;
  }
  
  return dotProduct / (normA * normB);
};

/**
 * 使用语义搜索过滤仓库
 * @param {Array} repos - 仓库列表
 * @param {string} keyword - 搜索关键词
 * @param {string} apiKey - Gemini API密钥
 * @param {number} threshold - 相似度阈值 (0 到 1 之间)
 * @param {boolean} includeReadme - 是否包含README内容
 * @returns {Promise<Array>} - 返回过滤后的仓库列表，每个仓库包含相似度信息
 */
export const semanticSearch = async (repos, keyword, apiKey, threshold = 0.7, includeReadme = false) => {
  if (!keyword || keyword.trim() === '') {
    return repos.map(repo => ({ repo, similarity: 1 }));
  }

  if (!apiKey) {
    throw new Error('未提供Gemini API密钥，无法使用语义搜索功能');
  }
  
  try {
    // 获取关键词的嵌入向量
    const keywordEmbedding = await getEmbedding(keyword, apiKey);
    console.log('关键词向量类型和长度:', typeof keywordEmbedding, Array.isArray(keywordEmbedding), keywordEmbedding?.length);
    
    // 确保关键词向量是数组格式
    let keywordEmbeddingArray = keywordEmbedding;
    if (!Array.isArray(keywordEmbedding) && typeof keywordEmbedding === 'object') {
      if (keywordEmbedding.values && Array.isArray(keywordEmbedding.values)) {
        keywordEmbeddingArray = keywordEmbedding.values;
      } else if (keywordEmbedding.embedding && Array.isArray(keywordEmbedding.embedding.values)) {
        keywordEmbeddingArray = keywordEmbedding.embedding.values;
      } else {
        console.error('无法将关键词向量转换为数组:', keywordEmbedding);
        throw new Error('关键词向量格式无效');
      }
    }
    
    if (!Array.isArray(keywordEmbeddingArray)) {
      throw new Error('关键词向量格式无效');
    }

    // 创建进度事件
    const progressEvent = new CustomEvent('semanticSearchProgress', {
      detail: { total: repos.length, current: 0, phase: 'preparing' }
    });
    window.dispatchEvent(progressEvent);
    
    // 批量处理仓库，每批20个
    const batchSize = 50;
    const results = [];
    let processedCount = 0;
    
    for (let i = 0; i < repos.length; i += batchSize) {
      const batch = repos.slice(i, i + batchSize);
      
      // 准备这一批的文本
      const batchTexts = batch.map(repo => {
        // 提取README的前1000个字符，避免文本过长
        const readmeExcerpt = includeReadme && repo.readme ? repo.readme.substring(0, 1000) : '';
        
        return {
          repo,
          text: [
            repo.name,
            repo.description || '',
            repo.language || '',
            repo.owner?.login || '',
            (repo.topics || []).join(' '),
            readmeExcerpt // 添加README摘要（如果包含）
          ].join(' ').trim()
        };
      }).filter(item => item.text); // 过滤掉空文本
      
      try {
        // 批量获取嵌入向量
        const genAI = new GoogleGenerativeAI(apiKey);
        const model = genAI.getGenerativeModel({ model: "embedding-001" });
        
        // 使用batchEmbedContents进行批量处理
        const embeddings = await Promise.all(
          batchTexts.map(async ({ text }) => {
            try {
              const result = await model.embedContent(text);
              return result.embedding;
            } catch (error) {
              console.error('获取嵌入向量失败:', error);
              return null;
            }
          })
        );

        // 计算相似度并保存结果
        for (let j = 0; j < batchTexts.length; j++) {
          const { repo } = batchTexts[j];
          const embedding = embeddings[j];
          
          if (embedding) {
            try {
              const similarity = cosineSimilarity(keywordEmbeddingArray, embedding);
              results.push({ repo, similarity });
            } catch (error) {
              console.error('计算相似度失败:', error, '仓库:', repo.name);
              results.push({ repo, similarity: 0 });
            }
          } else {
            results.push({ repo, similarity: 0 });
          }
          
          // 更新进度
          processedCount++;
          window.dispatchEvent(new CustomEvent('semanticSearchProgress', {
            detail: {
              total: repos.length,
              current: processedCount,
              phase: 'processing',
              currentRepo: repo.name
            }
          }));
        }
      } catch (error) {
        console.error('批处理失败:', error);
        // 如果批处理失败，将这批仓库的相似度设为0
        batch.forEach(repo => {
          results.push({ repo, similarity: 0 });
          processedCount++;
          window.dispatchEvent(new CustomEvent('semanticSearchProgress', {
            detail: {
              total: repos.length,
              current: processedCount,
              phase: 'processing',
              currentRepo: repo.name,
              error: error.message
            }
          }));
        });
      }
      
      // 在批次之间添加短暂延迟，避免API限制
      if (i + batchSize < repos.length) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }
    
    // 发送完成事件
    window.dispatchEvent(new CustomEvent('semanticSearchProgress', {
      detail: { total: repos.length, current: repos.length, phase: 'complete' }
    }));
    
    // 返回所有结果，不进行阈值过滤，让调用者决定如何过滤
    return results.sort((a, b) => b.similarity - a.similarity);
  } catch (error) {
    console.error('语义搜索失败:', error);
    // 发送错误事件
    window.dispatchEvent(new CustomEvent('semanticSearchProgress', {
      detail: { phase: 'error', error: error.message }
    }));
    throw error;
  }
};

// 添加限流控制
const requestQueue = [];
let processingQueue = false;
const RATE_LIMIT = 1000; // 每次请求之间的最小间隔（毫秒）

const processQueue = async () => {
  if (processingQueue || requestQueue.length === 0) return;
  
  processingQueue = true;
  while (requestQueue.length > 0) {
    const { text, apiKey, resolve, reject } = requestQueue.shift();
    try {
      const result = await getEmbeddingInternal(text, apiKey);
      resolve(result);
    } catch (error) {
      reject(error);
    }
    await new Promise(resolve => setTimeout(resolve, RATE_LIMIT));
  }
  processingQueue = false;
};

// 内部实际执行嵌入的函数
const getEmbeddingInternal = async (text, apiKey) => {
  if (!apiKey) {
    throw new Error('未提供Gemini API密钥');
  }

  try {
    const genAI = new GoogleGenerativeAI(apiKey);
    const model = genAI.getGenerativeModel({ model: "embedding-001" });
    const result = await model.embedContent(text);
    
    console.log('API返回的原始结果:', result);
    
    // 检查result是否有效
    if (!result) {
      console.error('API返回结果无效:', result);
      throw new Error('获取嵌入向量失败：API返回结果无效');
    }

    // 获取embedding值
    let embedding = null;
    
    // 处理不同的返回格式
    if (result.embedding && Array.isArray(result.embedding.values)) {
      embedding = result.embedding.values;
    } else if (result.embedding && typeof result.embedding === 'object') {
      embedding = Object.values(result.embedding);
    } else if (Array.isArray(result.values)) {
      embedding = result.values;
    } else if (typeof result === 'object' && 'values' in result) {
      embedding = result.values;
    }

    // 如果仍然没有找到有效的向量，尝试其他可能的属性
    if (!embedding) {
      const possibleKeys = ['embedding', 'vector', 'embeddings', 'value'];
      for (const key of possibleKeys) {
        if (result[key] && Array.isArray(result[key])) {
          embedding = result[key];
          break;
        }
      }
    }

    // 如果还是没有找到向量，抛出错误
    if (!embedding) {
      console.error('找不到嵌入向量:', result);
      throw new Error('API返回结果中找不到嵌入向量');
    }

    // 确保所有元素都是数字
    if (!embedding.every(value => typeof value === 'number')) {
      console.error('嵌入向量包含非数字元素:', embedding);
      throw new Error('嵌入向量必须只包含数字');
    }

    console.log('成功获取嵌入向量，长度:', embedding.length);
    return embedding; // 直接返回数组格式的向量
  } catch (error) {
    console.error('获取嵌入向量失败:', error);
    if (error.message.includes('PERMISSION_DENIED')) {
      throw new Error('API密钥无效或没有访问权限');
    } else if (error.message.includes('QUOTA_EXCEEDED')) {
      throw new Error('API配额已超限');
    } else if (error.message.includes('INVALID_ARGUMENT')) {
      throw new Error('无效的输入参数');
    }
    throw error;
  }
}; 